import { Formik, FormikProps } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import type { FC } from 'react'
import { ObjectSchema, ValidationError } from 'yup'
import ExpeditionEditForm, { ExternalFormProps } from '@components/forms/ExpeditionEditForm/ExpeditionEditForm'
import getOr from 'lodash/fp/getOr'
import { FormikHandleSubmit, getFormikHelpers } from '@typings/Formik'
import { createValidationSchema } from './validation'
import { ExpeditionDetail, ExpeditionDetailError, ExpeditionDetailTouched } from '@typings/entities/Expedition'
import { transformExpeditionForSubmit } from '@components/forms/ExpeditionEditForm/helpers'

type Props = ExternalFormProps & {
  expeditionId?: string
  initialValues?: Partial<ExpeditionDetail>
  initialErrors?: ExpeditionDetailError
  initialTouched?: ExpeditionDetailTouched
  onSave: FormikHandleSubmit<ExpeditionDetail>
  onSend: FormikHandleSubmit<ExpeditionDetail>
  onSaveAndSend: FormikHandleSubmit<ExpeditionDetail>
  hasDuplicatePositions?: boolean
  reloadExpedition: () => void
}

const initialValuesEmpty = {}

const ExpeditionEditFormFormik: FC<Props> = ({
  expeditionId,
  initialValues = initialValuesEmpty,
  initialErrors,
  initialTouched: initialTouchedProp,
  onSave,
  onSend,
  onSaveAndSend,
  ...formProps
}) => {
  const [differentDeliveryAddress, setDifferentDeliveryAddress] = useState(
    getOr(false, 'differentDeliveryAddress', initialValues),
  )

  const [cod, setCod] = useState(getOr(false, 'cod', initialValues))

  const [serviceFieldsMandatory, setServiceFieldsMandatory] = useState(() => {
    const service = formProps.carrierServices?.find((service) => service.id === initialValues.carrierService)
    const pickupPlace = formProps.pickupPlacesData?.find((pickupPlace) => pickupPlace.id === initialValues.carrierPickupPlace)

    return {
      pickupPlace: !!service?.pickupPlaceMandatory,
      externalCarrierPickupPlace: pickupPlace?.type === 'carrier_service_with_points',
      email: !!service?.emailMandatory,
      phone: !!service?.phoneMandatory,
      name: !!service?.nameMandatory,
    }
  })

  const handleValidationSchemaChange = useCallback(
    (carrierPickupPlace = null) => {
      const pickupPlace = formProps.pickupPlacesData?.find(
        (place) => place.id === (carrierPickupPlace || initialValues.carrierPickupPlace),
      )

      const mandatory = {
        ...serviceFieldsMandatory,
        externalCarrierPickupPlace: pickupPlace?.type === 'carrier_service_with_points',
      }

      setServiceFieldsMandatory(mandatory)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formProps.pickupPlacesData, initialValues.carrierPickupPlace],
  )

  useEffect(() => {
    handleValidationSchemaChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formProps.pickupPlacesData, initialValues.carrierPickupPlace])

  const handleValidate = useCallback(
    (values: ExpeditionDetail) => {
      const transformedValues = transformExpeditionForSubmit(values)

      const schema = createValidationSchema(differentDeliveryAddress, cod, serviceFieldsMandatory) as ObjectSchema<any>

      try {
        schema.validateSync(transformedValues, { abortEarly: false })
        return {}
      } catch (err) {
        if (err instanceof ValidationError) {
          return err.inner.reduce<Record<string, string>>((errors, error) => {
            if (error.path) {
              errors[error.path] = error.message
            }
            return errors
          }, {})
        }
        return {}
      }
    },
    [differentDeliveryAddress, cod, serviceFieldsMandatory],
  )

  const handleSubmit = useCallback(
    (
      { button, ...values }: ExpeditionDetail & { button?: string },
      formikHelpers: { setSubmitting: (isSubmitting: boolean) => void; setErrors: (errors: object) => void },
    ) => {
      const transformedValues = transformExpeditionForSubmit(values)

      switch (button) {
        case 'save':
          onSave(transformedValues, formikHelpers)
          break
        case 'send':
          onSend(transformedValues, formikHelpers)
          break
        case 'saveAndSend':
          onSaveAndSend(transformedValues, formikHelpers)
          break
      }
    },
    [onSave, onSend, onSaveAndSend],
  )

  const initialTouched = React.useMemo(() => {
    const initialTouched = initialTouchedProp || {}
    initialTouched.items = (initialValues?.items || []).map((item: object, index: number) => {
      return { bookAdviceTotal: true, ...(initialTouched.items?.[index] || {}) }
    })
    return initialTouched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTouchedProp])

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      initialErrors={initialErrors}
      initialTouched={initialTouched}
      validate={handleValidate}
      validateOnMount
      enableReinitialize
    >
      {(formikProps: FormikProps<ExpeditionDetail>) => {
        return (
          <ExpeditionEditForm
            expeditionId={expeditionId}
            {...formikProps}
            {...formProps}
            formikHelpers={getFormikHelpers(formikProps)}
            setServiceFieldsMandatory={setServiceFieldsMandatory}
            setDifferentDeliveryAddress={setDifferentDeliveryAddress}
            setCod={setCod}
            handleValidationSchemaChange={handleValidationSchemaChange}
          />
        )
      }}
    </Formik>
  )
}

export default ExpeditionEditFormFormik
